import { createStyles, LinearProgress, Theme, WithStyles, withStyles } from '@freighthub/silhouette-core'
import { Property } from 'csstype'
import { computed, Lambda, observable, observe } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Component } from 'react'
import Dropzone from 'react-dropzone'
import { UIStore } from 'service/UIStore'
import { I18N, i18n, I18nInterface } from 'utils/localisation'

import { DocumentInterface } from '../Documents'

export type ProgressEventHandler = (e: ProgressEvent) => void

const getBaseContainerStyle = (theme: Theme) => ({
  width: '100%',
  height: '100px',
  borderWidth: theme.spacing(0.25),
  borderColor: theme.palette.table.headerColor,
  borderStyle: 'dashed',
  borderRadius: theme.spacing(0.75),
  background: theme.palette.grey[200],

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column' as Property.FlexDirection,
})

const jssStyles = (theme: Theme) =>
  createStyles({
    container: getBaseContainerStyle(theme),
    dropContainer: {
      ...getBaseContainerStyle(theme),
      cursor: 'pointer',
      padding: theme.spacing(1),
      userSelect: 'none',
    },
    progressContainer: {
      ...getBaseContainerStyle(theme),
      alignItems: 'stretch',
      height: 'auto',
      minHeight: '100px',
    },
    progressItem: {
      margin: theme.spacing(1, 2),
    },
  })

interface IDocumentsUploadInject {
  uiStore: UIStore
}

export interface IDocumentsUploadProps extends Partial<IDocumentsUploadInject>, WithStyles<typeof jssStyles> {
  allowedFormats?: string[]
  multi?: boolean
  onAllUploaded?: () => void
  height?: string
  upload: (file: IFile, onProgress: ProgressEventHandler) => Promise<DocumentInterface>
  handleError: (errMessage: string) => void
}

interface IFile {
  name: string
}

export interface IFileTrack {
  id: number
  name: string
  progress: number
}

export class LeanDocumentsUpload extends Component<IDocumentsUploadProps & I18nInterface> {
  private fileId = 0
  private fileTracks = observable.array<IFileTrack>([])
  // @ts-expect-error TS(2564) FIXME: Property 'disposer' has no initializer and is not ... Remove this comment to see the full error message
  private disposer: Lambda

  componentDidMount() {
    this.disposer = observe(this.fileTracks, (change) => {
      if (change.object.length === 0) {
        this.props.onAllUploaded && this.props.onAllUploaded()
      }
    })
  }

  componentWillUnmount() {
    this.disposer()
  }

  @computed
  private get loading() {
    return this.fileTracks.length > 0
  }

  private handleError = (e?: string): void => {
    const errMessage = e || 'Error occurred while uploading file'
    this.props.handleError(errMessage)
  }

  onDrop = (acceptedFiles: IFile[]) => {
    const {
      allowedFormats,
      multi,
      uiStore,
      // @ts-expect-error TS(2339) FIXME: Property 'translate' does not exist on type 'Local... Remove this comment to see the full error message
      localisationStore: { translate },
    } = this.props
    if (multi !== true && acceptedFiles.length > 1) {
      const error = `Uploading of multiple files at the same time not supported for this Dropzone.`
      this.handleError(error)
      return
    }

    if (allowedFormats) {
      // eslint-disable-next-line no-restricted-syntax -- TODO: Consider not using a ForOfStatement, loops should be avoided in favor of array iterations
      for (const aFile of acceptedFiles) {
        const isFileSupported: boolean = allowedFormats.some((format) => aFile.name.toLowerCase().endsWith(format))
        if (!isFileSupported) {
          const fileName = aFile.name
          const fileTypes = allowedFormats.join(', ')
          const error = `Unsupported file format for file ${fileName}. Supported: ${fileTypes}`
          this.handleError(error)
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          uiStore.snack.addSnack({
            type: 'error',
            message: translate('documents-upload-error_message', { fileName, fileTypes }),
          })
          return
        }
      }
    }

    return Promise.all(acceptedFiles.map(this.uploadOnefile))
  }

  private uploadOnefile = (file: IFile) => {
    const fileTrack = observable({
      id: this.fileId,
      name: file.name,
      progress: 0,
    })
    this.fileId += 1
    this.fileTracks.push(fileTrack)
    return this.props
      .upload(file, (e) => this.onProgress(fileTrack, e))
      .then(() => this.onUploaded(fileTrack))
      .catch(this.handleError)
  }

  private onUploaded(fileTrack: IFileTrack) {
    this.fileTracks.remove(fileTrack)
  }

  private onProgress = (fileTrack: IFileTrack, e: ProgressEvent) => {
    const progress = (e.loaded / e.total) * 100
    fileTrack.progress = progress
  }

  render() {
    const { classes, height, allowedFormats } = this.props
    const heightStyle = height ? { height } : {}
    return (
      <div>
        {this.loading ? (
          <div className={classes.progressContainer}>
            {this.fileTracks.map((file) => (
              <UploadProgress key={file.id} fileName={file.name} progress={file.progress} />
            ))}
          </div>
        ) : (
          <Dropzone onDrop={this.onDrop}>
            {({ getRootProps, getInputProps }) => (
              <section {...getRootProps()} className={classes.dropContainer} style={heightStyle}>
                <input
                  {...getInputProps()}
                  data-e2e-id="select-upload_document-input"
                  data-testid="select-upload_document-input"
                  accept={allowedFormats?.join(',')}
                />
                <I18N term="frontend-sdk_click_or_drag_upload" />
              </section>
            )}
          </Dropzone>
        )}
      </div>
    )
  }
}

export const DocumentsUpload = inject('uiStore')(withStyles(jssStyles)(i18n(observer(LeanDocumentsUpload))))

interface IUploadProgressProps extends WithStyles<typeof jssStyles> {
  fileName: string
  progress: number
}
export class LeanUploadProgress extends Component<IUploadProgressProps> {
  render() {
    const { fileName, progress, classes } = this.props
    return (
      <div className={classes.progressItem}>
        <div>{fileName}</div>
        <LinearProgress variant={progress === 100 ? 'indeterminate' : 'determinate'} value={progress} />
      </div>
    )
  }
}

export const UploadProgress = withStyles(jssStyles)(observer(LeanUploadProgress))
